import { useMemo, useState } from 'react'
import {
  Alert,
  Button,
  Card,
  Checkbox,
  Col,
  DatePicker,
  Descriptions,
  Form,
  Input,
  Modal,
  Row,
  Select,
  Space,
  Statistic,
  Tag,
  Typography,
} from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { EyeOutlined } from '@ant-design/icons'
import { useMutation, useQuery } from '@tanstack/react-query'
import { type Dayjs } from 'dayjs'
import { useTranslation } from 'react-i18next'
import { useSearchParams } from 'react-router-dom'
import { queryKeys } from '@/shared/api/queryKeys'
import { defaultDayRange, rangeToIso } from '@/shared/lib/dayRange'
import { translateError } from '@/shared/api/errors'
import { formatDateTime, utcTooltip } from '@/shared/lib/format'
import { PageHeader } from '@/shared/ui/PageHeader'
import { DataTable } from '@/shared/ui/table/DataTable'
import { RowActions } from '@/shared/ui/table/RowActions'
import { fetchZones, fetchVenues } from '@/features/venue/venueApi'
import { submitOnValid } from '@/shared/lib/submitOnValid'
import {
  fetchCompliance,
  fetchJournalPage,
  revealIdentity,
  type Guest,
  type JournalEntry,
} from '@/features/venue/journalApi'

const { RangePicker } = DatePicker

/** ⚠️ TENANT zonasida: `dayjs()` brauzernikini olardi (`dayRange.ts`) */
const defaultRange = (): [Dayjs, Dayjs] => defaultDayRange(7)

const STRENGTH_COLOR: Record<string, string> = {
  SUFFICIENT: 'green',
  DELEGATED: 'blue',
  INSUFFICIENT: 'orange',
}

/**
 * Mehmonlar jurnali — IT vaziri buyrug'i №137 bo'yicha javob berish nuqtasi.
 *
 * ⚠️ TELEFON RO'YXATDA NIQOBLANGAN keladi (backend `Guest.masked()`).
 * To'liq ochish alohida amal — "Ochish" tugmasi sabab so'raydi va bu amal
 * HAR DOIM AUDIT'GA YOZILADI. Ochilgan qiymat jadval holatiga saqlanmaydi
 * (faqat natija oynasida ko'rsatiladi) — aks holda niqoblashning ma'nosi
 * yo'qolardi: sahifa qayta chizilganda ochiq raqam yana ekranda qolib
 * ketardi.
 */
export function VenueJournalPage() {
  const { t } = useTranslation()
  const [searchParams] = useSearchParams()

  const [range, setRange] = useState<[Dayjs, Dayjs]>(defaultRange)
  const [venueId, setVenueId] = useState<string | null>(searchParams.get('venueId'))
  const [zoneId, setZoneId] = useState<string | null>(null)
  const [phone, setPhone] = useState('')
  const [mac, setMac] = useState('')
  const [login, setLogin] = useState('')
  const [onlyInsufficient, setOnlyInsufficient] = useState(false)
  const [revealTarget, setRevealTarget] = useState<JournalEntry | null>(null)
  const [revealed, setRevealed] = useState<Guest | null>(null)

  const { from, to } = rangeToIso(range)

  const venues = useQuery({ queryKey: queryKeys.venues.all, queryFn: () => fetchVenues(null) })
  const zones = useQuery({
    queryKey: queryKeys.venues.zones(venueId ?? ''),
    queryFn: () => fetchZones(venueId ?? ''),
    enabled: venueId !== null,
  })

  const compliance = useQuery({
    queryKey: queryKeys.journal.compliance(venueId ?? '', from, to),
    queryFn: () => fetchCompliance(venueId ?? '', from, to),
    enabled: venueId !== null,
  })

  const filter = useMemo(
    () => ({ from, to, venueId, zoneId, phone, mac, login, onlyInsufficient }),
    [from, to, venueId, zoneId, phone, mac, login, onlyInsufficient],
  )

  /** Jadvalga tegadigan, KPI ga TEGMAYDIGAN filtrlar — pastdagi izoh sharti */
  const narrowed =
    zoneId !== null || phone !== '' || mac !== '' || login !== '' || onlyInsufficient
  const fetchPage = useMemo(() => fetchJournalPage(filter), [filter])

  const [reasonForm] = Form.useForm<{ reason: string }>()

  const reveal = useMutation({
    mutationFn: (values: { sessionId: string; reason: string }) =>
      revealIdentity(values.sessionId, values.reason),
    onSuccess: (guest) => {
      setRevealed(guest)
      setRevealTarget(null)
      reasonForm.resetFields()
    },
  })

  /**
   * Oynani yopish — xato xabari ham qolmasin: keyingi qatorni ochganda u
   * eski va begona bo'lardi.
   *
   * ⚠️ Xato mutatsiyaning O'ZIDA turadi (`VoucherVoidModal` naqshi), alohida
   * `useState` da emas. Ikkinchi nusxa ustunlar `useMemo` iga ham kirib
   * ketgan edi ("ochishdan oldin tozala") — endi u yerda tozalash kerak
   * emas, chunki yopishda allaqachon tozalanadi.
   */
  const closeReveal = () => {
    reveal.reset()
    setRevealTarget(null)
  }

  const columns = useMemo<ColumnsType<JournalEntry>>(
    () => [
      {
        key: 'startedAt',
        title: t('session.start'),
        width: 160,
        render: (_, row) => (
          <Typography.Text title={utcTooltip(row.session.startedAt)}>
            {formatDateTime(row.session.startedAt)}
          </Typography.Text>
        ),
      },
      {
        key: 'venue',
        title: t('nav.venues'),
        width: 160,
        render: (_, row) => (
          <Space direction="vertical" size={0}>
            <Typography.Text>{row.venueName}</Typography.Text>
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              {row.zoneName}
            </Typography.Text>
          </Space>
        ),
      },
      {
        key: 'guest',
        title: t('journal.guest'),
        width: 200,
        render: (_, row) => (
          <Space direction="vertical" size={0}>
            <Typography.Text code>{row.guest.phone ?? '—'}</Typography.Text>
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              {t(`identificationMethod.${row.guest.identityMethod}`)}
            </Typography.Text>
          </Space>
        ),
      },
      { key: 'mac', title: t('session.mac'), width: 150, render: (_, row) => row.session.mac ?? '—' },
      {
        key: 'strength',
        title: t('journal.strength'),
        width: 130,
        render: (_, row) => (
          <Tag color={STRENGTH_COLOR[row.session.identificationStrength]}>
            {t(`identificationStrength.${row.session.identificationStrength}`)}
          </Tag>
        ),
      },
      {
        key: 'login',
        title: t('journal.subscriberLogin'),
        width: 140,
        render: (_, row) => row.session.subscriberLogin ?? '—',
      },
      {
        key: 'status',
        title: t('subscribers.columns.status'),
        width: 110,
        render: (_, row) => (
          <Tag color={row.session.endedAt ? 'default' : 'green'}>
            {row.session.endedAt ? t('journal.ended') : t('journal.open')}
          </Tag>
        ),
      },
      {
        key: 'actions',
        title: '',
        width: 56,
        align: 'center',
        // ⚠️ `fixed: 'right'` — sababi `VenuesPage` da yozilgan. Bu
        // jadvalda holat eng yomon edi: "Ochish" havolasi HATTO 1440px
        // da ham ekrandan tashqarida qolardi (1564 > 1416).
        fixed: 'right',
        render: (_, row) => (
          <RowActions
            items={[
              {
                key: 'reveal',
                label: t('journal.reveal.button'),
                icon: <EyeOutlined />,
                onClick: () => setRevealTarget(row),
              },
            ]}
          />
        ),
      },
    ],
    [t],
  )

  return (
    <>
      <PageHeader
        title={t('nav.venueJournal')}
        description={t('journal.intro')}
        extra={
          <RangePicker
            value={range}
            onChange={(value) => {
              // ⚠️ Chekkalar bu yerda TEKISLANMAYDI: `rangeToIso` ularni
              // tenant zonasidagi kun boshi va kun OXIRIGA
              // (`23:59:59.999`) aylantiradi. Ilgari bu yerda
              // `startOf`/`endOf` turardi — vaqtsiz tanlagich ikkala
              // chekkani ham yarim tunga qo'yardi va tanlangan OXIRGI KUN
              // butunlay tashqarida qolardi. `defaultRange` da bu to'g'ri
              // edi, ya'ni nuqson faqat operator sanani QO'LDA
              // o'zgartirganda chiqardi. Endi ikkala yo'l ham bitta
              // funksiyadan o'tadi.
              if (value?.[0] && value[1]) setRange([value[0], value[1]])
            }}
            allowClear={false}
            format="DD.MM.YYYY"
          />
        }
      />

      <Card size="small" style={{ marginBottom: 16 }}>
        <Row gutter={[12, 12]}>
          <Col xs={24} sm={12} md={6}>
            <Select
              allowClear
              style={{ width: '100%' }}
              placeholder={t('journal.filterVenue')}
              value={venueId ?? undefined}
              loading={venues.isPending}
              options={(venues.data ?? []).map((v) => ({ value: v.id, label: v.name }))}
              onChange={(value: string | undefined) => {
                setVenueId(value ?? null)
                setZoneId(null)
              }}
            />
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Select
              allowClear
              disabled={venueId === null}
              style={{ width: '100%' }}
              placeholder={t('journal.filterZone')}
              value={zoneId ?? undefined}
              loading={zones.isFetching}
              options={(zones.data ?? []).map((z) => ({ value: z.id, label: z.name }))}
              onChange={(value: string | undefined) => setZoneId(value ?? null)}
            />
          </Col>
          <Col xs={24} sm={12} md={4}>
            <Input.Search
              allowClear
              placeholder={t('journal.filterPhone')}
              onSearch={(value) => setPhone(value.trim())}
            />
          </Col>
          <Col xs={24} sm={12} md={4}>
            <Input.Search
              allowClear
              placeholder={t('journal.filterMac')}
              onSearch={(value) => setMac(value.trim())}
            />
          </Col>
          <Col xs={24} sm={12} md={4}>
            <Input.Search
              allowClear
              placeholder={t('journal.filterLogin')}
              onSearch={(value) => setLogin(value.trim())}
            />
          </Col>
          <Col xs={24} sm={12} md={24}>
            <Checkbox checked={onlyInsufficient} onChange={(e) => setOnlyInsufficient(e.target.checked)}>
              {t('journal.onlyInsufficient')}
            </Checkbox>
          </Col>
        </Row>
      </Card>

      {venueId && compliance.data ? (
        <Card size="small" title={t('journal.complianceTitle')} style={{ marginBottom: 16 }}>
          {/*
            ⚠️ KPI JADVAL BILAN BIR XIL DOIRADA EMAS va buni EKRAN AYTADI.
            `GET /api/venues/journal/compliance` faqat `venueId`+`from`+`to`
            oladi (`journalApi.ts`), jadval esa yana besh filtrni yuboradi.
            «Faqat yetarsizlarini ko'rsat» yoqilganda pastda faqat nosoz
            sessiyalar, tepada esa «Rioya: 97%» turardi — bitta ekranda
            bir-biriga zid ikki javob va hech qanday izoh. Backend bu
            filtrlarni qabul qilmagani uchun KPI ni jadvalga ERGASHTIRISH
            imkoni yo'q; qolgan yagona halol yo'l — farqni ochiq aytish.
          */}
          {narrowed ? (
            <Alert
              type="info"
              showIcon
              style={{ marginBottom: 12 }}
              message={t('journal.complianceScopeTitle')}
              description={t('journal.complianceScopeHint')}
            />
          ) : null}
          <Row gutter={16}>
            <Col xs={12} md={6}>
              <Statistic title={t('journal.compliancePercent')} value={compliance.data.compliantPercent} precision={1} suffix="%" />
            </Col>
            <Col xs={12} md={6}>
              <Statistic title={t('journal.totalSessions')} value={compliance.data.totalSessions} />
            </Col>
            <Col xs={12} md={6}>
              <Statistic title={t('journal.insufficientSessions')} value={compliance.data.insufficientSessions} />
            </Col>
            <Col xs={12} md={6}>
              <Statistic title={t('journal.uniqueGuests')} value={compliance.data.uniqueGuests} />
            </Col>
          </Row>
        </Card>
      ) : null}

      <DataTable<JournalEntry>
        queryKey={queryKeys.journal.list(filter)}
        fetchPage={fetchPage}
        columns={columns}
        rowKey={(row) => row.session.id}
        tableId="venueJournal"
        scrollX={1300}
        emptyTitle={t('journal.empty')}
        emptyDescription={t('journal.emptyHint')}
      />

      <Modal
        open={revealTarget !== null}
        title={t('journal.reveal.title')}
        onCancel={closeReveal}
        onOk={() =>
          submitOnValid(reasonForm, (values) => {
            if (!revealTarget) return
            reveal.mutate({ sessionId: revealTarget.session.id, reason: values.reason.trim() })
          })
        }
        confirmLoading={reveal.isPending}
        okText={t('journal.reveal.confirm')}
        cancelText={t('common.cancel')}
        destroyOnHidden
      >
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message={t('journal.reveal.auditWarningTitle')}
          description={t('journal.reveal.auditWarningText')}
        />

        {reveal.isError ? (
          <Alert
            type="error"
            showIcon
            style={{ marginBottom: 16 }}
            message={translateError(reveal.error, t)}
          />
        ) : null}

        <Form form={reasonForm} layout="vertical" preserve={false}>
          <Form.Item
            name="reason"
            label={t('actions.reason')}
            extra={t('journal.reveal.reasonHint')}
            rules={[{ required: true, message: t('journal.reveal.reasonRequired') }]}
          >
            <Input.TextArea rows={3} maxLength={200} autoFocus />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        open={revealed !== null}
        title={t('journal.reveal.resultTitle')}
        onCancel={() => setRevealed(null)}
        footer={
          <Button type="primary" onClick={() => setRevealed(null)}>
            {t('common.close')}
          </Button>
        }
        destroyOnHidden
      >
        {revealed ? (
          <Descriptions column={1} size="small" bordered>
            <Descriptions.Item label={t('journal.phone')}>
              {revealed.phone ?? t('journal.reveal.anonymized')}
            </Descriptions.Item>
            <Descriptions.Item label={t('journal.fullName')}>
              {revealed.fullName ?? '—'}
            </Descriptions.Item>
            <Descriptions.Item label={t('journal.roomNumber')}>
              {revealed.roomNumber ?? '—'}
            </Descriptions.Item>
          </Descriptions>
        ) : null}
      </Modal>
    </>
  )
}
